import '../css/app.css';

import { createInertiaApp } from '@inertiajs/vue3';
import * as Sentry from '@sentry/vue';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { ZiggyVue } from 'ziggy-js';
import posthogPlugin from '../plugins/posthog';
import { initializeTheme } from './composables/useAppearance';
// import './echo'; // Import Echo for WebSockets
import { configureEcho } from '@laravel/echo-vue';
import { createLogger } from 'vue-logger-plugin';

configureEcho({
    broadcaster: 'pusher',
    key: import.meta.env.VITE_PUSHER_APP_KEY,
    cluster: import.meta.env.VITE_PUSHER_APP_CLUSTER,
});

// Extend ImportMeta interface for Vite...
declare module 'vite/client' {
    interface ImportMetaEnv {
        readonly VITE_APP_NAME: string;
        [key: string]: string | boolean | undefined;
    }

    interface ImportMeta {
        readonly env: ImportMetaEnv;
        readonly glob: <T>(pattern: string) => Record<string, () => Promise<T>>;
    }
}

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

createInertiaApp({
    title: (title) => `${title} - ${appName}`,
    resolve: (name) => resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });
        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN,
            // debug: true,
            environment: import.meta.env.VITE_APP_ENV,
            integrations: [
                Sentry.browserTracingIntegration(),
                //     Sentry.replayIntegration(),
                Sentry.captureConsoleIntegration(),
                //     Sentry.vueIntegration({app, attachProps: true})
            ],
            // Tracing
            tracesSampleRate: 1.0, //  Capture 100% of the transactions
            // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
            tracePropagationTargets: ['localhost', 'vab2025.laravel.cloud', 'vab2025.test'],
            // Session Replay
            replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
            replaysOnErrorSampleRate: 0.5, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
        });

        if (import.meta.env.VITE_POSTHOG_KEY) {
            app.use(posthogPlugin);
        }
        app.use(createLogger({ enabled: false }))
            .use(plugin)
            .use(ZiggyVue)
            .mount(el);

        return app;
    },
    progress: {
        color: '#4B5563',
    },
});

// This will set light / dark mode on page load...
initializeTheme();
